import { get as getMetadata, ref as metadataRef } from 'firebase/database'
import {
  endAt,
  endBefore,
  get,
  limitToFirst,
  limitToLast,
  onValue,
  orderByChild,
  query,
  ref,
  startAt,
  withSearch,
  type Unsubscribe,
} from '../../services/dashboard-read-view'

import { getFirebaseServices } from '../../services/firebase'
import {
  INBOX_QUERY_SIZE,
  MESSAGE_QUERY_SIZE,
  type InboxCursor,
  type MessageCursor,
} from './conversations-pagination'

export const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000
export const THREAD_RESOLUTION_QUERY_SIZE = 26
export const RECENT_THREAD_QUERY_SIZE = 25

export type ConversationRepository = {
  getAggregateReadiness?: (tenantId: string) => Promise<unknown>
  subscribeAggregateInbox?: (
    tenantId: string,
    value: (snapshot: unknown) => void,
    error: (error: unknown) => void
  ) => Unsubscribe
  getOlderAggregateInbox?: (
    tenantId: string,
    cursor: InboxCursor
  ) => Promise<unknown>
  // A page of the clinic's whole list holding only the rows that match
  // `search`, newest first, before `cursor` when one is given.
  searchAggregateInbox?: (
    tenantId: string,
    search: string,
    cursor: InboxCursor | null
  ) => Promise<unknown>
  searchInbox?: (
    tenantId: string,
    search: string,
    cursor: InboxCursor | null
  ) => Promise<unknown>
  subscribeInbox: (
    tenantId: string,
    value: (snapshot: unknown) => void,
    error: (error: unknown) => void
  ) => Unsubscribe
  getOlderInbox: (tenantId: string, cursor: InboxCursor) => Promise<unknown>
  getExactThread: (
    tenantId: string,
    patientId: string,
    threadId: string
  ) => Promise<unknown>
  getProducerThreadWindow: (
    tenantId: string,
    patientId: string,
    timestamp: number
  ) => Promise<unknown>
  getRecentThreads: (tenantId: string, patientId: string) => Promise<unknown>
  getExactVoiceCall: (
    tenantId: string,
    patientId: string,
    callId: string
  ) => Promise<unknown>
  subscribeMessages: (
    tenantId: string,
    patientId: string,
    threadId: string,
    value: (snapshot: unknown) => void,
    error: (error: unknown) => void
  ) => Unsubscribe
  getOlderMessages: (
    tenantId: string,
    patientId: string,
    threadId: string,
    cursor: MessageCursor
  ) => Promise<unknown>
}

function databaseRef(path: string) {
  return ref(getFirebaseServices().database, path)
}

async function queryValue(queryReference: ReturnType<typeof query>) {
  const snapshot = await get(queryReference)
  return snapshot.exists() ? (snapshot.val() as unknown) : null
}

export const firebaseConversationRepository: ConversationRepository = {
  async getAggregateReadiness(tenantId) {
    const snapshot = await getMetadata(
      metadataRef(
        getFirebaseServices().database,
        `dashboardInteractionAggregateReadiness/v1/${tenantId}`
      )
    )
    return snapshot.exists() ? (snapshot.val() as unknown) : null
  },

  subscribeAggregateInbox(tenantId, handleValue, handleError) {
    const inboxQuery = query(
      databaseRef(`dashboardInteractionAggregates/v1/${tenantId}`),
      orderByChild('display/latestActivityAtMs'),
      startAt(1),
      limitToLast(INBOX_QUERY_SIZE)
    )
    return onValue(
      inboxQuery,
      (snapshot) => handleValue(snapshot.exists() ? snapshot.val() : null),
      handleError
    )
  },

  getOlderAggregateInbox(tenantId, cursor) {
    return queryValue(
      query(
        databaseRef(`dashboardInteractionAggregates/v1/${tenantId}`),
        orderByChild('display/latestActivityAtMs'),
        startAt(1),
        endBefore(cursor.timestamp, cursor.patientId),
        limitToLast(INBOX_QUERY_SIZE)
      )
    )
  },

  searchAggregateInbox(tenantId, search, cursor) {
    return queryValue(
      withSearch(
        query(
          databaseRef(`dashboardInteractionAggregates/v1/${tenantId}`),
          orderByChild('display/latestActivityAtMs'),
          startAt(1),
          ...(cursor ? [endBefore(cursor.timestamp, cursor.patientId)] : []),
          limitToLast(INBOX_QUERY_SIZE)
        ),
        search
      )
    )
  },

  searchInbox(tenantId, search, cursor) {
    return queryValue(
      withSearch(
        query(
          databaseRef(`patients/${tenantId}`),
          orderByChild('lastMessage/timestamp'),
          startAt(1),
          ...(cursor ? [endBefore(cursor.timestamp, cursor.patientId)] : []),
          limitToLast(INBOX_QUERY_SIZE)
        ),
        search
      )
    )
  },

  subscribeInbox(tenantId, handleValue, handleError) {
    const inboxQuery = query(
      databaseRef(`patients/${tenantId}`),
      orderByChild('lastMessage/timestamp'),
      startAt(1),
      limitToLast(INBOX_QUERY_SIZE)
    )
    return onValue(
      inboxQuery,
      (snapshot) => handleValue(snapshot.exists() ? snapshot.val() : null),
      handleError
    )
  },

  getOlderInbox(tenantId, cursor) {
    return queryValue(
      query(
        databaseRef(`patients/${tenantId}`),
        orderByChild('lastMessage/timestamp'),
        startAt(1),
        endBefore(cursor.timestamp, cursor.patientId),
        limitToLast(INBOX_QUERY_SIZE)
      )
    )
  },

  async getExactThread(tenantId, patientId, threadId) {
    const snapshot = await get(
      databaseRef(`threads/${tenantId}/${patientId}/${threadId}`)
    )
    return snapshot.exists() ? (snapshot.val() as unknown) : null
  },

  getProducerThreadWindow(tenantId, patientId, timestamp) {
    return queryValue(
      query(
        databaseRef(`threads/${tenantId}/${patientId}`),
        orderByChild('startChatTs'),
        startAt(timestamp - TWO_DAYS_MS),
        endAt(timestamp),
        limitToFirst(THREAD_RESOLUTION_QUERY_SIZE)
      )
    )
  },

  getRecentThreads(tenantId, patientId) {
    return queryValue(
      query(
        databaseRef(`threads/${tenantId}/${patientId}`),
        orderByChild('startChatTs'),
        limitToLast(RECENT_THREAD_QUERY_SIZE)
      )
    )
  },

  async getExactVoiceCall(tenantId, patientId, callId) {
    const snapshot = await get(
      databaseRef(`calls/${tenantId}/${patientId}/${callId}`)
    )
    return snapshot.exists() ? (snapshot.val() as unknown) : null
  },

  subscribeMessages(tenantId, patientId, threadId, handleValue, handleError) {
    const messageQuery = query(
      databaseRef(`messages/${tenantId}/${patientId}/${threadId}`),
      orderByChild('timestamp'),
      startAt(1),
      limitToLast(MESSAGE_QUERY_SIZE)
    )
    return onValue(
      messageQuery,
      (snapshot) => handleValue(snapshot.exists() ? snapshot.val() : null),
      handleError
    )
  },

  getOlderMessages(tenantId, patientId, threadId, cursor) {
    return queryValue(
      query(
        databaseRef(`messages/${tenantId}/${patientId}/${threadId}`),
        orderByChild('timestamp'),
        startAt(1),
        endBefore(cursor.timestamp, cursor.messageId),
        limitToLast(MESSAGE_QUERY_SIZE)
      )
    )
  },
}

export function errorKind(error: unknown): 'forbidden' | 'transport' {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = String(error.code).toLowerCase()
    if (
      code.includes('permission-denied') ||
      code.includes('permission_denied')
    ) {
      return 'forbidden'
    }
  }
  return 'transport'
}
